import { LegalPage, LegalProse, LegalSection } from '../components/LegalProse';

/**
 * /child-safety — the published child safety standards (2026-11-03).
 *
 * Exists for the Play Console "Child safety standards" declaration: Google
 * rejected the Android app until a public, non-PDF page that names the app and
 * the developer explicitly prohibits child sexual abuse and exploitation (CSAE).
 * The Terms (§ User Content) and /community-guidelines carry the same rule in
 * general words; this page is the one the declaration links to.
 *
 * What it says the product does is edge behaviour:
 *   18+           → the 18+ tick before anyone talks, and the age check
 *                   (src/edge/ember/age-check.js): under 18 → the minor notice
 *                   (shared/minor-notice.js) and everything shared is deleted
 *   Report        → POST /api/rooms/:roomId/lines/:lineId/report and
 *                   /report-member, read in the review admin
 *   closing       → store.closeAccount (scripts/close-account.mjs): no restore,
 *                   rows kept (account-deletion/account-stopped.js)
 * Change the code and this page changes with it.
 */
export default function ChildSafety() {
  return (
    <LegalPage title="Child safety standards" dateline="Ember is a service of Oriscen, Inc. Last updated: November 3, 2026">

        <LegalProse>
          <p>
            Ember (Ember: Talk It Out on Google Play and the App Store, and the website at
            ember.space) is made by Oriscen, Inc. (Oriscen). These are our
            standards against child sexual abuse and exploitation (CSAE). They apply to everything
            on Ember: conversations with Ember, Embers, chats, rooms, Meet people, photos, and
            anything else anyone provides through the service.
          </p>

          <LegalSection title="Ember prohibits child sexual abuse and exploitation">
            <p>
              Oriscen has zero tolerance for CSAE. No one may use Ember to create, upload, share,
              request, or describe in a sexual way any of the following:
            </p>
            <ul>
              <li>child sexual abuse material (CSAM), including drawn, written, or AI-generated material</li>
              <li>any sexualization of a person under 18</li>
              <li>grooming: building trust with a minor, or with the adults around them, in order to abuse them</li>
              <li>sextortion: threatening a minor, or demanding images or money from them</li>
              <li>trafficking a minor, or advertising or arranging their sexual exploitation</li>
              <li>seeking contact with a minor for any sexual purpose</li>
            </ul>
            <p>
              Ember itself will not produce any of this. The same rule is part of our{' '}
              <a href="/terms">Terms of Service</a> and{' '}
              <a href="/community-guidelines">Community Guidelines</a>.
            </p>
          </LegalSection>

          <LegalSection title="Ember is for adults">
            <p>
              Ember is for people 18 and older. Everyone confirms they are 18 or older before they
              start talking with Ember. If someone says in a conversation that they are younger,
              Ember pauses and asks their age. If they are under 18, the account and everything they
              shared are deleted, and they are shown places where young people can get support.
            </p>
          </LegalSection>

          <LegalSection title="What we do when we find CSAE">
            <ul>
              <li>We remove the content.</li>
              <li>We close the account. It cannot be restored.</li>
              <li>
                We report apparent child sexual abuse material to the National Center for Missing
                &amp; Exploited Children (NCMEC) through its CyberTipline, as US law requires, and
                cooperate with law enforcement.
              </li>
              <li>We keep what the law requires us to keep for that report.</li>
            </ul>
          </LegalSection>

          <LegalSection title="How to report">
            <p>
              In a chat or a room, tap <strong>Report</strong> on any line or member. The line is
              hidden for you at once, and our team reviews it.
            </p>
            <p>
              For anything else, email{' '}
              <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a> with the words "Child
              safety" in the subject. Do not attach or forward images of a child; describe where you
              saw it and we will find it.
            </p>
            <p>
              If a child is in immediate danger, call 911 or your local emergency number. You can
              also report child sexual exploitation directly to NCMEC at{' '}
              <a href="https://report.cybertip.org/">report.cybertip.org</a> or 1-800-843-5678.
            </p>
          </LegalSection>

          <LegalSection title="Child safety contact">
            <p>
              Oriscen's child safety point of contact can be reached at{' '}
              <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>. We comply with child
              safety laws where Ember is offered, including reporting to NCMEC.
            </p>
          </LegalSection>
        </LegalProse>
    </LegalPage>
  );
}
